import { useQuery } from "@tanstack/react-query";
import { Card, MIN_MODAL_CONTENT_HEIGHT, PieChart } from "components";
import { useMemo, useState } from "react";
import {
  ChartExport,
  useExportableChart,
} from "../../../../../../../../shared/components";
import { Props } from "../../../../WebAnalytics.types";
import { getCustomersByRevenueSize } from "../../Visualization.requests";

export const CustomersByRevenueSize = ({
  pixelId,
  accountView,
  campaignId,
  startDate,
  endDate,
  accountsIds,
  visitedPages,
  includeIspTraffic,
  includeKnownBotTraffic,
  includePotentialBotTraffic,
  personas,
}: Props) => {
  const { data: rawData, isFetching: isFetching } = useQuery({
    queryKey: [
      "webAnalyticsCustomersByRevenueSize",
      pixelId,
      accountView,
      campaignId,
      startDate,
      endDate,
      accountsIds,
      visitedPages,
      includeIspTraffic,
      includeKnownBotTraffic,
      includePotentialBotTraffic,
      personas,
    ],
    queryFn: () =>
      getCustomersByRevenueSize(pixelId, {
        accountView,
        campaignId,
        startDate,
        endDate,
        accountsIds,
        visitedPages,
        includeIspTraffic,
        includeKnownBotTraffic,
        includePotentialBotTraffic,
        personas,
      }),
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const data = useMemo(() => {
    if (isFetching || !rawData?.data) {
      return [];
    }

    return rawData.data.map(({ value, label }) => ({
      label,
      value: Number(value),
    }));
  }, [rawData, isFetching]);

  const chart = useExportableChart({
    title: "Accounts Visited by Revenue Size",
    campaignId: campaignId || undefined,
    chart: (
      <PieChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        legendPosition="bottom"
        data={data ?? []}
        displayAsPercent={true}
      />
    ),
    dates: {
      start: startDate,
      end: endDate,
    },
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isFetching}
      title={chart.title}
      hasData={!isFetching && Boolean(data?.length)}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={campaignId || undefined}
          title={chart.title}
          onExportPNG={chart.downloadAsPng}
          data={data}
        />
      }
    >
      {chart.component}
      {chart.exportComponent}
    </Card>
  );
};
